import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';

/**
 * Floating "Added to cart" notification card. Shown after any successful add-to-cart on the
 * storefront — product page, quick add, or a collection card's inline buy buttons all funnel
 * through the same `product-form-component` (assets/product-form.js), which dispatches a
 * `CartLinesUpdateEvent` with `action: 'add', context: 'product'` on success. This component is
 * the single global listener for that event; it does not care which form triggered it.
 *
 * Reads the added line's details out of the resolved cart (from `detail.items`, the raw
 * `/cart.json`-shaped array `#refreshCart()` already fetches in product-form.js) rather than
 * re-fetching anything itself.
 *
 * @typedef {object} CartAddedNotificationRefs
 * @property {HTMLElement} card
 * @property {HTMLImageElement | undefined} image
 * @property {HTMLElement} title
 * @property {HTMLElement} variants
 * @property {HTMLElement} cartCount
 * @property {HTMLElement | undefined} giftHint
 * @extends Component<CartAddedNotificationRefs>
 */
export class CartAddedNotification extends Component {
  requiredRefs = ['card', 'title', 'variants', 'cartCount'];

  #abortController = new AbortController();

  /** @type {number | undefined} */
  #autoCloseTimeout;

  /** Product id of the line the card is currently showing, so a late gift can be matched to it. */
  #shownProductId = '';

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });
    // See assets/buy-x-get-y-sync.js: fired the instant the gift's own add succeeds, a full
    // network round trip before #maybeShowGift's event (below) resolves. This is what makes the
    // gift row appear alongside the product instead of a beat later.
    document.addEventListener('buy-x-get-y:gift-preview', this.#onGiftPreview, { signal });
    // The other outcome: the product did not earn the gift. See #onGiftPending.
    document.addEventListener('buy-x-get-y:gift-pending', this.#onGiftPending, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    if (this.#autoCloseTimeout) clearTimeout(this.#autoCloseTimeout);
  }

  /**
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    // The Buy X Get Y gift is added a beat later than the product, by assets/buy-x-get-y-sync.js,
    // under its own 'standard-action' context — so it arrives as a second event rather than in the
    // one above. Fold it into the notification that's already on screen instead of ignoring it or
    // popping a second card, so the shopper is actually told what they got.
    if (event.context === 'standard-action') {
      this.#maybeShowGift(event);
      return;
    }

    if (event.action !== 'add' || event.context !== 'product') return;

    const merchandiseId = event.lines?.[0]?.merchandiseId;
    if (!merchandiseId) return;

    // merchandiseId is a gid (e.g. "gid://shopify/ProductVariant/123") by the time the event
    // constructor has run; /cart.json line items key off the plain numeric variant id.
    const variantId = merchandiseId.toString().split('/').pop();

    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;

        const items = /** @type {Array<Record<string, any>>} */ (detail?.items ?? []);
        const item = items.find((line) => String(line.id) === String(variantId));
        if (!item) return;

        this.#show(item, items);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-added-notification] Event promise rejected:', error);
      });
  };

  /**
   * The early signal: assets/buy-x-get-y-sync.js dispatches this the moment the gift's own
   * `/cart/add.js` call succeeds, carrying that response's own line item data (title, image,
   * quantity — everything the row needs). Firing before the slower settle fetch that
   * #maybeShowGift waits on is the entire point; whether the shopper sees this depends only on how
   * fast that one add request comes back, not on a chain of three sequential ones.
   *
   * @param {CustomEvent<{gift: Record<string, any>}>} event
   */
  #onGiftPreview = (event) => {
    const gift = event.detail?.gift;
    if (!gift) return;
    if (String(gift.properties?._gift_for_product_id ?? '') !== this.#shownProductId) return;

    this.#renderGift(gift);
  };

  /**
   * The product that was just added does not earn the free gift yet, so the gift was NOT added.
   * assets/buy-x-get-y-sync.js sends the finished message (how much more, of which collection) a
   * moment after the add, once it has measured the cart — it is shown under the product, and the card
   * is kept open long enough to read it.
   *
   * @param {CustomEvent<{productId: string, message: string}>} event
   */
  #onGiftPending = (event) => {
    const { productId, message } = event.detail ?? {};
    if (!message || String(productId) !== this.#shownProductId) return;

    const { card, giftHint } = this.refs;
    if (card.hidden || !giftHint) return;

    giftHint.textContent = message;
    giftHint.hidden = false;
    this.#startAutoClose();
  };

  /**
   * Reveals the free-gift row once the gift for the product currently on screen actually lands in
   * the cart. Keyed on `_gift_for_product_id` rather than "whatever gift line is newest", so a cart
   * that already held a different gift can't make this name the wrong one.
   *
   * By the time this fires, #onGiftPreview above has usually already shown the row — this pass
   * mainly exists to correct `cartCount` against the now-fully-settled cart (the preview's own
   * add-to-cart response doesn't know the cart's total item count) and to catch the gift on the
   * rare path that skips the preview event.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #maybeShowGift(event) {
    const productId = this.#shownProductId;
    if (!productId) return;

    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;

        const items = /** @type {Array<Record<string, any>>} */ (detail?.items ?? []);
        const giftLines = items.filter(
          (line) => line.properties?._free_gift && String(line.properties?._gift_for_product_id) === productId
        );
        if (giftLines.length === 0) return;

        // All the units the cart now holds for this gift (several may have been earned, and the
        // discount may spread them over more than one line).
        const giftQuantity = giftLines.reduce((sum, line) => sum + (Number(line.quantity) || 0), 0);
        this.#renderGift(giftLines[0], giftQuantity);

        // The count shown a moment ago predates the gift line, and the preview event's own add
        // response has no notion of the cart's total — only the now-settled cart does.
        const { cartCount } = this.refs;
        const total = items.reduce((sum, line) => sum + (Number(line.quantity) || 0), 0);
        if (cartCount) cartCount.textContent = String(total);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[cart-added-notification] Gift promise rejected:', error);
      });
  }

  /**
   * @param {Record<string, any>} gift
   * @param {number} [quantity] - Units of the gift in the cart; defaults to the line's own.
   */
  #renderGift(gift, quantity = Number(gift.quantity) || 1) {
    const { card, gift: giftRow, giftImage, giftTitle, giftHint } = this.refs;
    if (card.hidden || !giftRow) return;

    // A gift is in the cart, so whatever said it wasn't is out of date.
    if (giftHint) giftHint.hidden = true;

    if (giftImage && gift.image) {
      giftImage.src = gift.image;
      giftImage.alt = gift.product_title ?? '';
      giftImage.hidden = false;
    } else if (giftImage) {
      giftImage.hidden = true;
    }

    if (giftTitle) {
      const title = gift.product_title ?? gift.title ?? '';
      giftTitle.textContent = quantity > 1 ? `${title} × ${quantity}` : title;
    }
    giftRow.hidden = false;
  }

  /**
   * @param {Record<string, any>} item
   * @param {Array<Record<string, any>>} items
   */
  #show(item, items) {
    const { card, image, title, variants, cartCount, gift, giftHint } = this.refs;

    this.#shownProductId = String(item.product_id ?? '');
    // A fresh add starts with no gift shown; #maybeShowGift reveals it if one arrives.
    if (gift) gift.hidden = true;
    if (giftHint) giftHint.hidden = true;

    if (image && item.image) {
      image.src = item.image;
      image.alt = item.product_title ?? '';
      image.hidden = false;
    } else if (image) {
      image.hidden = true;
    }

    title.textContent = item.product_title ?? item.title ?? '';

    variants.replaceChildren();
    if (this.dataset.showVariants === 'true') {
      /** @type {Array<{name: string, value: string}>} */
      const options = item.options_with_values ?? [];
      for (const option of options) {
        if (!option?.value || option.value === 'Default Title') continue;
        const line = document.createElement('p');
        line.className = 'cart-added-notification__variant';
        line.textContent = `${option.name}: ${option.value}`;
        variants.appendChild(line);
      }
    }

    const totalQuantity = items.reduce((sum, line) => sum + (Number(line.quantity) || 0), 0);
    cartCount.textContent = String(totalQuantity);

    card.hidden = false;
    // Restart the reveal animation on repeat adds.
    card.classList.remove('cart-added-notification__card--visible');
    // eslint-disable-next-line no-unused-expressions
    card.offsetHeight;
    card.classList.add('cart-added-notification__card--visible');

    this.#startAutoClose();
  }

  /**
   * (Re)starts the auto-close countdown and its shrinking bar from full. Called when the card opens
   * and again when a late message lands on it, so nothing the shopper has yet to read is closed on them.
   */
  #startAutoClose() {
    const { progressTrack, progressBar } = this.refs;

    if (this.#autoCloseTimeout) clearTimeout(this.#autoCloseTimeout);
    const autoCloseSeconds = Number(this.dataset.autoCloseSeconds) || 0;

    if (autoCloseSeconds > 0) {
      this.#autoCloseTimeout = setTimeout(() => this.closeNotification(), autoCloseSeconds * 1000);

      if (progressTrack && progressBar) {
        progressTrack.hidden = false;
        // Restart the shrink animation from full width, same reflow trick as the card reveal.
        progressBar.style.animation = 'none';
        // eslint-disable-next-line no-unused-expressions
        progressBar.offsetHeight;
        progressBar.style.animation = '';
        progressBar.style.animationDuration = `${autoCloseSeconds}s`;
      }
    } else if (progressTrack) {
      progressTrack.hidden = true;
    }
  }

  closeNotification() {
    const { card } = this.refs;
    card.classList.remove('cart-added-notification__card--visible');
    if (this.#autoCloseTimeout) clearTimeout(this.#autoCloseTimeout);

    setTimeout(() => {
      card.hidden = true;
    }, 250);
  }
}

if (!customElements.get('cart-added-notification')) {
  customElements.define('cart-added-notification', CartAddedNotification);
}
